import { useState } from 'react';
import { StickyNote, Edit3, Save } from 'lucide-react';

/** StatusPill — card compacto exibindo KPI com ícone */
export function StatusPill({ icon, label, value, color, bg }: {
  icon: React.ReactNode; label: string; value: number | string; color: string; bg: string;
}) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '8px 4px', borderRadius: 10, background: bg, border: `1px solid ${color}18`,
      minWidth: 0, textAlign: 'center',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, color }}>
        <div style={{ flexShrink: 0 }}>{icon}</div>
        <span style={{ fontSize: '1rem', fontWeight: 800, lineHeight: 1 }}>{value}</span>
      </div>
      <div style={{ fontSize: '0.58rem', color: '#6b7280', fontWeight: 500, marginTop: 3, whiteSpace: 'nowrap' }}>{label}</div>
    </div>
  );
}

/** ActionBtn — botão de ação com hover colorido */
export function ActionBtn({ icon, label, accent, onClick }: {
  icon: React.ReactNode; label: string; accent: string; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderRadius: 10,
        border: `1px solid ${accent}22`, background: `${accent}08`, cursor: 'pointer',
        fontSize: '0.82rem', fontWeight: 600, color: accent, transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = `${accent}15`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = `${accent}08`; e.currentTarget.style.transform = 'none'; }}
    >
      {icon} {label}
    </button>
  );
}

/** NotasProfessor — bloco de anotações pessoais do dia (localStorage) */
export function NotasProfessor({ professorId }: { professorId?: string }) {
  const dataKey = `notas-prof-${professorId || 'anon'}-${new Date().toISOString().split('T')[0]}`;
  const [nota, setNota] = useState(() => localStorage.getItem(dataKey) || '');
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  const salvar = () => {
    localStorage.setItem(dataKey, nota);
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div style={{ background: 'var(--bg-card, white)', borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-color, #e5e7eb)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <StickyNote size={13} color="#f59e0b" />
          <h3 style={{ fontSize: '0.78rem', fontWeight: 700, margin: 0 }}>Minhas Notas</h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {saved && <span style={{ fontSize: '0.65rem', color: '#16a34a', fontWeight: 600 }}>✓ Salvo</span>}
          {editing ? (
            <button onClick={salvar} style={{ border: 'none', background: '#3b82f6', color: 'white', borderRadius: 6, padding: '4px 10px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Save size={11} /> Salvar
            </button>
          ) : (
            <button onClick={() => setEditing(true)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center' }}>
              <Edit3 size={13} color="#94a3b8" />
            </button>
          )}
        </div>
      </div>
      <div style={{ padding: '10px 14px' }}>
        {editing ? (
          <textarea
            value={nota}
            onChange={e => setNota(e.target.value)}
            placeholder="Escreva suas anotações do dia aqui..."
            autoFocus
            style={{
              width: '100%', minHeight: 120, border: '1px solid #e2e8f0', borderRadius: 8,
              padding: '10px 12px', fontSize: '0.8rem', fontFamily: 'inherit', resize: 'vertical',
              background: '#fefce8', color: '#1e293b', outline: 'none', lineHeight: 1.6,
            }}
            onKeyDown={e => { if (e.key === 'Escape') { setEditing(false); } }}
          />
        ) : (
          <div
            onClick={() => setEditing(true)}
            style={{
              minHeight: 60, fontSize: '0.8rem', color: nota ? '#334155' : '#94a3b8',
              cursor: 'pointer', borderRadius: 8, padding: '8px 4px',
              lineHeight: 1.6, whiteSpace: 'pre-wrap',
            }}
          >
            {nota || 'Clique para adicionar notas do dia...'}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Estilos reutilizáveis ─────────────────────────────────────

export const btnWhite: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
  background: 'white', color: '#1e293b', border: 'none', fontWeight: 700, fontSize: '0.78rem',
  cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'transform 0.15s',
};

export const btnGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
  background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)',
  fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap',
  transition: 'all 0.15s',
};
